/* Setup and Extra pages: Broking Details, Settings, Help.
 *
 * Each is a set of reference cards. Nothing here invents data: the broking
 * page lists the accounts the API grants, settings change what the client
 * itself controls, and help documents what exists.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { api } from '@/lib/api'
import { Icon } from '@/components/Icons'
import { Card, KvRow, ModeDot, Tile } from '@/panels/dashboardBits'
import { fmtMoney } from '@/lib/format'
import type { NavGroup } from '@/components/Terminal'
import type { TradingMode } from '@/types'

type Account = Awaited<ReturnType<typeof api.accounts>>['items'][number]

/* ---------------------------------------------------------- broking details */
export function BrokingDetails() {
  const [rows, setRows] = useState<Account[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    api.accounts()
      .then((r) => { if (!cancelled) setRows(r.items) })
      .catch(() => { if (!cancelled) { setRows([]); setError('Accounts could not be loaded.') } })
    return () => { cancelled = true }
  }, [])

  const live = (rows ?? []).filter((a) => a.trading_mode === 'LIVE').length
  const tradable = (rows ?? []).filter((a) => a.can_trade).length
  const cash = (rows ?? []).reduce((a, r) => a + (Number(r.cash_balance) || 0), 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
      <div className="qx-tiles">
        <Tile tone="blue" icon="wallet" value={rows ? String(rows.length) : '—'} label="Accounts"
              info="Accounts visible to this user" />
        <Tile tone="green" icon="check" value={rows ? String(tradable) : '—'} label="Tradable by you"
              info="Accounts on which you hold a TRADE or MANAGE grant" />
        <Tile tone="red" icon="alert" value={rows ? String(live) : '—'} label="Live accounts"
              info="Accounts in LIVE mode send real orders" />
        <Tile tone="teal" icon="coins" value={rows ? fmtMoney(cash, 'INR', true) : '—'} label="Cash balance"
              info="Sum of cash across visible accounts" />
      </div>

      <Card title="Trading accounts"
            info="Every account is created in PAPER mode. Enabling live trading is an administrator's step-up action with a recorded reason."
            noPadding>
        <div className="scroll-x">
          <table className="qx" style={{ fontSize: 'var(--fs-sm)' }}>
            <thead>
              <tr>
                <th>Mode</th><th>Code</th><th>Name</th><th>Currency</th><th>Live enabled</th>
                <th className="r">Cash</th><th className="r">Margin available</th>
                <th className="r">Margin used</th><th className="r">Utilisation</th>
                <th>Your access</th>
              </tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={10} className="qx-empty">Loading…</td></tr>}
              {rows !== null && rows.length === 0 && (
                <tr><td colSpan={10} className="qx-empty">{error ?? 'No accounts are granted to this user. Ask an administrator for access.'}</td></tr>
              )}
              {(rows ?? []).map((a) => {
                const util = a.margin_utilization_pct == null ? null : Number(a.margin_utilization_pct)
                return (
                  <tr key={a.id}>
                    <td><ModeDot mode={a.trading_mode as TradingMode} /></td>
                    <td className="num" style={{ fontWeight: 700 }}>{a.code}</td>
                    <td>{a.name}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{a.base_currency}</td>
                    <td>
                      <span className={`qx-status ${a.live_enabled ? 'FAILED' : 'RUNNING'}`}>
                        {a.live_enabled ? 'YES' : 'NO'}
                      </span>
                    </td>
                    <td className="num r">{fmtMoney(a.cash_balance, a.base_currency)}</td>
                    <td className="num r">{fmtMoney(a.margin_available, a.base_currency)}</td>
                    <td className="num r">{fmtMoney(a.margin_used, a.base_currency)}</td>
                    <td className="num r" style={{ color: util != null && util > 80 ? 'var(--warn)' : undefined }}>
                      {util == null ? '—' : `${util.toFixed(1)}%`}
                    </td>
                    <td>
                      <span style={{ fontWeight: 700, color: a.can_trade ? 'var(--ok)' : 'var(--text-muted)' }}>
                        {a.my_access ?? 'none'}{a.can_trade ? ' · can trade' : ''}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

/* ------------------------------------------------------------------ settings */
export function SettingsPage({ theme, onTheme, email, pages }: {
  theme: 'dark' | 'light'
  onTheme: (t: 'dark' | 'light') => void
  email?: string
  pages: NavGroup[]
}) {
  return (
    <div style={{
      display: 'grid', gap: 'var(--sp-4)',
      gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', alignItems: 'start',
    }}>
      <Card title="Appearance" info="The choice is stored in this browser and applies to the terminal and the admin console alike.">
        <div className="qx-inset">
          <div className="qx-kv">
            <span className="qx-kv-label"><Icon name="sun" size={16} /> Theme</span>
            <span className="qx-chips">
              <button className={`qx-chip${theme === 'light' ? ' on' : ''}`} onClick={() => onTheme('light')}>Light</button>
              <button className={`qx-chip${theme === 'dark' ? ' on' : ''}`} onClick={() => onTheme('dark')}>Dark</button>
            </span>
          </div>
          <KvRow icon="keyboard" label="Typeface" value="Open Sans" sub="tabular figures in every number" tone="neutral" />
        </div>
      </Card>

      <Card title="Session" info="Signing out is in the account menu at the top right.">
        <div className="qx-inset">
          <KvRow icon="user" label="Signed in as" value={email ?? '—'} tone="neutral" />
          <KvRow icon="lock" label="Session" value="Refreshed automatically" sub="a 401 from any request ends it" tone="neutral" />
        </div>
      </Card>

      <ShortcutsCard pages={pages} />
    </div>
  )
}

/* ---------------------------------------------------------------------- help */
export function HelpPage({ pages, onLicences }: { pages: NavGroup[]; onLicences: () => void }) {
  return (
    <div style={{
      display: 'grid', gap: 'var(--sp-4)',
      gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', alignItems: 'start',
    }}>
      <Card title="Getting around" info="What each section of the sidebar is for.">
        <div className="qx-inset">
          {pages.map((g) => (
            <div key={g.group} className="qx-kv" style={{ alignItems: 'flex-start' }}>
              <span className="qx-kv-label" style={{ minWidth: 150 }}>{g.group}</span>
              <span style={{ textAlign: 'right', fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                {g.items.map((p) => <span key={p.id} style={{ display: 'block' }}><b style={{ color: 'var(--text-primary)' }}>{p.label}</b> — {p.info}</span>)}
              </span>
            </div>
          ))}
        </div>
      </Card>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
        <ShortcutsCard pages={pages} />

        <Card title="Open-source notices"
              info="The charting library's licence requires an attribution and link to be reachable from the application. This is where it lives."
              footer={<button className="qx-btn qx-btn-outline" onClick={onLicences}>Licences &amp; attributions</button>}>
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
            Qorvex is built on open-source software. The full set of notices is in
            THIRD-PARTY-NOTICES at the repository root; the ones carrying a user-visible
            requirement are shown in the dialog.
          </p>
        </Card>

        <Card title="Support" info="Where to go when something is wrong.">
          <div className="qx-inset">
            <KvRow icon="alert" label="Rejected orders" value="Bell icon, top right" tone="neutral" sub="opens the order blotter" />
            <KvRow icon="power" label="Emergency stop" value="Kill switch, status bar" tone="danger" sub="always visible, never scrolled away" />
            <KvRow icon="headset" label="Access or accounts" value="Your administrator" tone="neutral" sub="account grants and live enablement are admin actions" />
          </div>
        </Card>
      </div>
    </div>
  )
}

function ShortcutsCard({ pages }: { pages: NavGroup[] }) {
  const keyed = pages.flatMap((g) => g.items).filter((p) => p.key)
  return (
    <Card title="Keyboard shortcuts" info="Function keys switch pages; ⌘K or Ctrl+K opens search and commands." noPadding>
      <table className="qx" style={{ fontSize: 'var(--fs-sm)' }}>
        <thead><tr><th>Key</th><th>Action</th></tr></thead>
        <tbody>
          <tr><td><Kbd>⌘K</Kbd> / <Kbd>Ctrl+K</Kbd></td><td>Search instruments and commands</td></tr>
          {keyed.map((p) => (
            <tr key={p.id}><td><Kbd>{p.key}</Kbd></td><td>{p.label}</td></tr>
          ))}
          <tr><td><Kbd>Esc</Kbd></td><td>Close a dialog or menu</td></tr>
        </tbody>
      </table>
    </Card>
  )
}

function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd style={{
      padding: '1px 6px', background: 'var(--bg-active)', borderRadius: 4,
      fontSize: 'var(--fs-2xs)', fontFamily: 'var(--font-code)', color: 'var(--accent)', fontWeight: 700,
    }}>
      {children}
    </kbd>
  )
}
